/**
 * metricDefinitions.js
 *
 * Single source of truth for what every analytics card means, written for
 * non-technical partners. Every metric on the page references one of these
 * via <MetricTooltip {...METRIC_DEFINITIONS.X} />.
 *
 * Editorial rules:
 *   • Title: short, sentence case, what the partner sees on the card.
 *   • Body: one plain sentence, no jargon. Avoid "gallery" — use staged floor plan / link.
 *   • Sections: optional. Use {label, text} pairs for "How we calculate it"
 *     and "What's excluded" so the math is transparent without being scary.
 *   • Always say what's INCLUDED and what's NOT — partners shouldn't have
 *     to guess.
 */

const PIPELINE_SCOPE_NOTE = {
  label: 'Pipeline scope',
  text: 'Counts every floor plan upload in this period, including MagicFurnish-assisted production on agent listings. This is supply activity, not buyer engagement.',
};

const DEMAND_SCOPE_NOTE = {
  label: 'Buyer engagement scope',
  text: 'Counts opens, shuffles, listing interest, and shares from visitors and guests, including partner agents on @serhant.com. Use Filters to exclude partner agents or @magicfurnish.com internal testing. Recent Activity below always shows everything.',
};

const ACCOUNT_SCOPE_NOTE = {
  label: 'Sign-up scope',
  text: 'Counts account-created events in this period. @magicfurnish.com sign-ups are hidden when the internal toggle is on.',
};

const METRIC_DEFINITIONS = {
  submissions: {
    title: 'Floor Plan Submissions',
    body: 'A floor plan file was submitted and sent to the AI pipeline for processing.',
    sections: [
      {
        label: 'What this card shows',
        text: 'Every floor plan uploaded in this period, and how it ended up — processed successfully or need attention. Not every upload is tied to a listing.',
      },
      {
        label: 'How we calculate it',
        text: 'Each row is one upload attempt. Processed and Need Attention are the green and red outcomes in the list. The headline total is their sum (plus any still processing). The ring is success rate: Processed ÷ (Processed + Need Attention).',
      },
      {
        label: "What needs attention",
        text: 'Client-side validation errors, broken uploads, and any plan that never reached the ready-to-view stage.',
      },
      PIPELINE_SCOPE_NOTE,
    ],
  },

  processingTime: {
    title: 'Average Processing Time',
    body: 'How long it typically takes from upload to when the staged floor plan is ready to view and share.',
    sections: [
      {
        label: 'How we calculate it',
        text: 'For each successful upload we measure upload-to-ready time, then average the results. Pairs faster than 5 seconds are ignored as instant retries.',
      },
      {
        label: 'Includes only successes',
        text: 'Failed submissions never become ready, so they are not in this average. The "did not finish" count next to the time tells you how many failures landed in this window.',
      },
      PIPELINE_SCOPE_NOTE,
    ],
  },

  uniqueVisitors: {
    title: 'Unique Visitors',
    body: 'How many distinct people opened a staged floor plan link in this period — counted once per person even if they opened several links.',
    sections: [
      {
        label: 'How we identify a visitor',
        text: 'Each browser gets a stable hidden ID, or we use their email if they are signed in. We only count opens we can attribute to one of those.',
      },
      DEMAND_SCOPE_NOTE,
    ],
  },

  linkOpens: {
    title: 'Link Opens',
    body: 'How many times someone loaded a staged floor plan page. Quick returns right after leaving the editor are not counted here — they show up in interactive sessions instead.',
    sections: [
      {
        label: 'What this counts',
        text: 'Each time someone lands on a staged floor plan link, except when they just tapped Done in the editor and came back to the same plan within a couple of minutes — that back-and-forth is captured by interactive sessions, not by this count.',
      },
      {
        label: 'Not per listing unless linked',
        text: 'Unless a floor plan is linked to a listing ID, we cannot tell which MLS listing an open belongs to — only that it happened on your MagicFurnish subdomain.',
      },
    ],
  },

  shuffles: {
    title: 'Shuffles',
    body: 'Total layout variations users generated in this period. Each shuffle creates one new arrangement of the same room.',
    sections: [
      {
        label: 'What the caption shows',
        text: 'The line below restates the total alongside the interactive-session count from the rest of the dashboard, so you can see how concentrated or spread out the engagement was.',
      },
      DEMAND_SCOPE_NOTE,
    ],
  },

  conversion: {
    title: 'Sign-Ups & Conversion',
    body: 'MagicFurnish accounts created in this period — while viewing a staged floor plan, or from your standalone sign-up page.',
    sections: [
      {
        label: 'While viewing a floor plan',
        text: 'A visitor was in the editor or on a staged floor plan link and created a MagicFurnish account to save progress. This may be an agent, a buyer, or a client copy — not necessarily the original MLS listing upload.',
      },
      {
        label: 'From the sign-up page',
        text: 'Someone created an account via your /signup page without an open floor plan — often agents or team members setting up access.',
      },
      {
        label: 'About the sign-up list',
        text: 'Each row is one account-created event. Name and email appear when we have them. Activity stats (uploads, shuffles, shares) are for that person in this period.',
      },
      {
        label: 'Not unique people',
        text: 'The headline count is sign-up events, not deduplicated users — the same person testing twice will appear twice.',
      },
      ACCOUNT_SCOPE_NOTE,
    ],
  },

  shares: {
    title: 'Shares',
    body: 'How many times users copied a floor plan\'s gallery link to share it.',
    sections: [
      {
        label: 'What this counts',
        text: 'Each time someone copies the gallery link is one share. Tapping Share opens the share modal but is not itself counted — the share is recorded when the link is actually copied, from either the editor or the gallery.',
      },
      {
        label: 'What you\'ll see in the list',
        text: 'User copied the gallery link to share it (shown as Gallery Link Copied in Recent Activity).',
      },
      DEMAND_SCOPE_NOTE,
    ],
  },

  inquiries: {
    title: 'Listing interest',
    body: 'How many times a viewer tapped I\'m Interested on a staged floor plan in this period.',
    sections: [
      {
        label: 'What this counts',
        text: 'Each first time someone taps I\'m Interested on a floor plan link and shares contact info. Repeat submits from the same person on the same plan are not counted again.',
      },
      {
        label: 'What you\'ll see in the list',
        text: 'Their email and which agent\'s listing they expressed interest in, with a link to open that floor plan.',
      },
      DEMAND_SCOPE_NOTE,
    ],
  },

  exports: {
    title: 'Downloads',
    body: 'How many times someone saved the furnished layout as an image — typically agents creating marketing collateral.',
    sections: [
      {
        label: 'What this counts',
        text: 'User-initiated image downloads only. Internal saves and copies do not count — only when the user explicitly chose to download.',
      },
      {
        label: 'Not the same as listing interest',
        text: 'Downloads are usually agents saving marketing images. Tapping I\'m Interested is a direct request to connect — the strongest visitor signal on MagicFurnish.',
      },
      {
        label: 'What you\'ll see in the list',
        text: 'User downloaded the furnished floor plan as an image.',
      },
      DEMAND_SCOPE_NOTE,
    ],
  },

  recentActivity: {
    title: 'Recent Activity',
    body: 'Live feed of what is happening on your subdomain. Each card is one staged floor plan link (uploads, agent edits, and client copies are separate cards).',
    sections: [
      {
        label: 'How events are grouped',
        text: 'One card per floor plan link. Repeated shuffles are bundled with an "N×" badge. Moving between the floor plan view and the editor appears as separate rows in the timeline.',
      },
      {
        label: 'Client copies',
        text: 'When a client creates their own layout from a shared link, that is a new link and a new card — not merged into the agent\'s original upload unless both are linked to the same listing ID.',
      },
    ],
  },

  editorSessionsCard: {
    title: 'Interactive Sessions',
    body: 'Visits where someone actually used the editor — furnished, shuffled, or added items — and how long they stayed actively engaged.',
    sections: [
      {
        label: 'What counts as a session',
        text: 'At least one meaningful action (auto-furnish, shuffle, or add items) and at least 5 seconds of active time. Multiple editor opens in the same visit count as one session.',
      },
      {
        label: 'Typical vs average',
        text: 'Typical is the median: half of sessions were shorter, half were longer. Average usually runs higher than typical because a few long sessions pull it up — that gap is real engagement, not a bug.',
      },
      {
        label: 'Active time, not wall-clock',
        text: 'We add up the time between recorded actions and remove any gap longer than 2 minutes (stepped away, phone screen locked, etc.). A tab left open does not inflate the number.',
      },
      {
        label: 'About "Long session" (15+ min)',
        text: 'Sessions with 15 or more minutes of active editing. These are the strongest engagement signal on the dashboard.',
      },
      DEMAND_SCOPE_NOTE,
    ],
  },

  typicalSession: {
    title: 'Typical session',
    body: 'The median active editing time per session. Half of sessions were shorter than this, half were longer.',
    sections: [
      {
        label: 'How active time works',
        text: 'We add up the time between consecutive actions and treat any gap longer than 2 minutes as idle — so a tab left open between shuffles does not pad the number.',
      },
      {
        label: 'Why we show typical and average together',
        text: 'Typical (median) is what most people experienced. Average can run higher when a few long sessions stretch it up. Showing both makes the skew visible instead of hiding it.',
      },
      {
        label: "What's excluded",
        text: 'Sessions shorter than 5 seconds and sessions with no meaningful actions.',
      },
    ],
  },

  madeEdits: {
    title: 'Made Edits',
    body: 'Of the editor opens that lasted more than 5 seconds, the percentage where the user actually did something meaningful.',
    sections: [
      {
        label: 'What counts as a meaningful action',
        text: 'Auto-furnish, shuffle, or adding items by hand. Just opening the editor and closing it again does not count.',
      },
    ],
  },

  interactiveActiveTime: {
    title: 'Interactive Session (Active Time)',
    body: 'A visit measured as one journey — opening the editor, editing, returning to the floor plan view, and opening the editor again.',
    sections: [
      {
        label: 'How a visit is built',
        text: 'A visit starts the first time the user opens the editor. Every additional editor open within 30 minutes belongs to the same visit. The visit ends at the user\'s last recorded action.',
      },
      {
        label: 'Why active time',
        text: 'We add up the time the user was actively doing things and remove gaps longer than 2 minutes, which we treat as idle (stepped away, phone screen locked, etc.).',
      },
      {
        label: 'Why this number is "canonical"',
        text: 'The interactive-session count from this section is the same number shown in the Editor Sessions header and the Shuffles card caption.',
      },
    ],
  },

  editorVisitsPerInteractive: {
    title: 'Editor Visits per Session',
    body: 'How many times the user opened the editor during a single interactive session, on average.',
    sections: [
      {
        label: 'Why this matters',
        text: 'Users often edit, tap Done to preview the floor plan, and edit again. A value above 1 means they are iterating.',
      },
    ],
  },

  listings: {
    title: 'Listings',
    body: "Per-listing engagement once a floor plan is linked to a listing ID in Floor plans. Link the original agent upload — client copies share the parent's listing.",
    sections: [
      {
        label: 'How listings are identified',
        text: "A listing ID (for example from Luxury Presence) linked in the Floor plans section to the original agent upload. Client copies share the parent's listing — do not link them separately.",
      },
      {
        label: 'Status & days on market',
        text: 'Status and days on market come from your brokerage site when it sends updates to MagicFurnish. If a row shows Unknown or —, we have not received that data yet for that listing.',
      },
      {
        label: 'Listing interest',
        text: 'Contacts shared via I\'m Interested, tied to that listing in the date range you picked at the top of the page. Zero means none in that period, not an error.',
      },
      {
        label: 'Shares & downloads',
        text: 'Per-listing counts for that property only, including activity from client copies of the linked floor plan. Totals at the top of the page still include all floor plans on your site.',
      },
      {
        label: 'What is not a listing row',
        text: 'Standalone agent uploads and one-off tests without a linked listing ID appear only under Recent Activity, not here.',
      },
    ],
  },

  platformUsage: {
    title: 'Platform Usage',
    body: 'Monthly AI furnishing generations used out of the plan\'s allowance. This follows the calendar month, not the date range you pick at the top of the page.',
    sections: [
      {
        label: 'How we count',
        text: 'Every successful and failed generation request counts against the monthly quota. The bar resets at the start of each calendar month.',
      },
      {
        label: 'Not tied to your date filter',
        text: 'The date range at the top of the dashboard (Last 7 days, etc.) does not change this section — it always shows the current calendar month and recent months.',
      },
    ],
  },

  eventGlossary: {
    title: 'Event Glossary',
    body: 'Plain-English meaning of every event type we track, with the count for the selected period.',
  },

  creatorActivity: {
    title: 'Analytics filters',
    body: 'Optional toggles to shape buyer engagement and sign-up KPIs without hiding activity from Recent Activity.',
    sections: [
      {
        label: 'Exclude @magicfurnish.com',
        text: 'Removes MagicFurnish staff testing from buyer engagement and sign-up KPIs. Pipeline uploads always include MF-assisted production.',
      },
      {
        label: 'Exclude @serhant.com',
        text: 'Removes partner-agent activity from buyer engagement KPIs so counts reflect external visitors only. Off by default — Serhant agents are included unless you turn this on.',
      },
      {
        label: 'Recent Activity is always complete',
        text: 'The live feed and CSV export always show every session regardless of these toggles.',
      },
    ],
  },
};

export default METRIC_DEFINITIONS;
